import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  FaEdit, 
  FaSearch,
  FaGift,
  FaTag,FaEye
} from 'react-icons/fa';
import {
  TextField,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Typography,
  Grid,
  Box,
  IconButton,
  Card,
  Chip,
  InputAdornment,
  FormControlLabel,
  Switch,
  CircularProgress,
  Pagination,
  ToggleButton,
  Skeleton,
  Alert
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { MdDeleteForever } from "react-icons/md";
import { postData, getData, deleteData, putData } from '../../Webservice/webservice';

// Styled Components
const VoucherCard = styled(Card)(({ theme }) => ({
  position: 'relative',
  borderRadius: '16px',
  overflow: 'hidden',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  cursor: 'pointer',
  display: 'flex',
  flexDirection: 'row',
  border: '1px solid #e0e0e0',
  boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
  height: '100%',
  minHeight: '160px',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 12px 24px rgba(0,0,0,0.1)',
    borderColor: 'transparent'
  }
}));

const CardLeftSection = styled(Box)(({ theme }) => ({
  width: '120px',
  background: '#f8f9fa',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  borderRight: '1px dashed #e0e0e0',
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    right: '-10px',
    top: '50%',
    transform: 'translateY(-50%)',
    width: '20px',
    height: '20px',
    background: '#f8fafc', // Matches page background
    borderRadius: '50%',
    boxShadow: 'inset 2px 0 4px rgba(0,0,0,0.05)'
  }
}));

const CardRightSection = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: '16px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  position: 'relative',
  backgroundColor: '#ffffff'
}));

const CouponCodeBox = styled(Box)(({ theme }) => ({
  background: '#fff',
  border: '1px dashed #2B333A',
  borderRadius: '6px',
  padding: '4px 12px',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  fontFamily: 'Mulish, sans-serif',
  fontWeight: 700,
  fontSize: '0.9rem',
  color: '#2B333A',
  marginBottom: '8px'
}));

const FormContainer = styled(Box)(({ theme }) => ({
  backgroundColor: '#ffffff',
  borderRadius: '12px',
  padding: theme.spacing(4),
  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
}));

const CouponsManagement = () => {
  const navigate = useNavigate();
  const { couponId } = useParams();
  const [coupons, setCoupons] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('All');
  const [showForm, setShowForm] = useState(false);
  const [currentCoupon, setCurrentCoupon] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [meta, setMeta] = useState({ page: 1, totalPages: 1, total: 0, limit: 20 });
  const [formMode, setFormMode] = useState('edit');
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);

  // Validation errors state
  const [errors, setErrors] = useState({});

  // Form state
  const [formData, setFormData] = useState({
    code: '',
    type: 'percentage',
    discount: '',
    maxDiscount: '',
    minOrderAmount: '',
    usageLimit: '',
    perUserLimit: '',
    validFrom: '',
    validTill: '',
    isFirstTimeUserOnly: false,
    applicableTo: '',
    applicableCardTypes: [''],
    allowedCardBins: [''],
    status: 'active',
    role_id: '',
    assignTo: ''
  });

  const fetchCoupons = async (page = 1, limit = 20) => {
    setLoading(true);
    setError(null);
    try {
      const tokenValue = sessionStorage.getItem('token');
      const token = tokenValue ? `Bearer ${tokenValue}` : '';
      const res = await getData('coupon/list', { page, limit }, token, false);
      
      if (res && res.coupons && Array.isArray(res.coupons)) {
        const transformedCoupons = res.coupons.map(coupon => ({
          id: coupon._id,
          code: coupon.code,
          type: coupon.type,
          discount: coupon.discount,
          maxDiscount: coupon.maxDiscount || null,
          minOrderAmount: coupon.minOrderAmount,
          usageLimit: coupon.usageLimit,
          perUserLimit: coupon.perUserLimit,
          validFrom: coupon.validFrom,
          validTill: coupon.validTill,
          isFirstTimeUserOnly: coupon.isFirstTimeUserOnly,
          applicableTo: coupon.applicableTo ,
          applicableCardTypes: coupon.applicableCardTypes || [],
          allowedCardBins: coupon.allowedCardBins || [],
          status: coupon.status,
          used: coupon.usedBy ? coupon.usedBy.length : 0,
          assignTo: coupon.assignTo || null,
          role_id: coupon.assignTo?.role_id?._id || coupon.assignTo?.role_id?.id || null
        }));
        setCoupons(transformedCoupons);
        setMeta(res.meta || { page: 1, totalPages: 1, total: 0, limit });
      } else {
        setCoupons([]);
        setError('Failed to fetch coupons');
      }
    } catch (err) {
      setCoupons([]);
      setError('Failed to fetch coupons. Please try again.');
      console.error('Error fetching coupons:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEditForm = useCallback((coupon) => {
    setIsEditing(true);
    setCurrentCoupon(coupon);
    setFormMode('view');
    setFormData({
      code: coupon.code,
      type: coupon.type || 'percentage',
      discount: coupon.discount,
      maxDiscount: coupon.maxDiscount || '',
      minOrderAmount: coupon.minOrderAmount || '',
      usageLimit: coupon.usageLimit || '',
      perUserLimit: coupon.perUserLimit || '',
      validFrom: coupon.validFrom || '',
      validTill: coupon.validTill || '',
      isFirstTimeUserOnly: coupon.isFirstTimeUserOnly || false,
      applicableTo: coupon.applicableTo || '',
      applicableCardTypes: coupon.applicableCardTypes || [''],
      allowedCardBins: coupon.allowedCardBins || [''],
      status: coupon.status || 'active',
      role_id: coupon.role_id || '',
      assignTo: coupon.assignTo || ''
    });
    setErrors({});
    setShowForm(true);
    if (!couponId || couponId !== (coupon.id || coupon._id)) {
      navigate(`/coupons/${coupon.id || coupon._id}`);
    }
  }, [couponId, navigate]);

  useEffect(() => {
    fetchCoupons(meta.page, meta.limit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta.page, meta.limit]);

  useEffect(() => {
    if (!couponId) return;
    if (loading) return;
    if (!coupons || coupons.length === 0) return;
    const match = coupons.find(c => c.id === couponId || c._id === couponId);
    if (match) {
      handleOpenEditForm(match);
    }
  }, [couponId, coupons, loading, handleOpenEditForm]);
  
  useEffect(() => {
    if (!showForm) return;
    const tokenValue = sessionStorage.getItem('token');
    const token = tokenValue ? `Bearer ${tokenValue}` : '';
    getData('user/role', undefined, token, false).then((res) => {
      if (res && Array.isArray(res)) {
        setRoles(res);
      } else if (res && Array.isArray(res.roles)) {
        setRoles(res.roles);
      }
    });
    getData('user/list', undefined, token, false).then((res) => {
      if (res && Array.isArray(res)) {
        setUsers(res);
      } else if (res && Array.isArray(res.users)) {
        setUsers(res.users);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showForm]);
  

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleFilterChange = (e) => {
    setFilter(e.target.value);
  };

  const handleOpenAddForm = () => {
    setIsEditing(false);
    setCurrentCoupon(null);
    setFormMode('edit');
    setFormData({
      code: '',
      type: 'percentage',
      discount: '',
      maxDiscount: '',
      minOrderAmount: '',
      usageLimit: '',
      perUserLimit: '',
      validFrom: '',
      validTill: '',
      isFirstTimeUserOnly: false,
      applicableTo: '',
      applicableCardTypes: [''],
      allowedCardBins: [''],
      status: 'active',
      role_id: '',
      assignTo: ''
    });
    setErrors({});
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    navigate('/coupons');
  };

  const handleFormModeChange = () => {
    setFormMode(prevMode => prevMode === 'view' ? 'edit' : 'view');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'role_id' ? { assignTo: '' } : {})
    }));
  };

  const filteredUsers = useMemo(() => {
    if (!formData.role_id) return users;
    return users.filter((u) => {
      const userRoleId = typeof u.role_id === 'object' && u.role_id !== null ? (u.role_id._id || u.role_id.id) : u.role_id;
      return userRoleId === formData.role_id;
    });
  }, [users, formData.role_id]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.code || !formData.code.toString().trim()) {
      newErrors.code = 'Coupon code is required';
    }

    const discountVal = formData.discount === '' || formData.discount === null ? '' : Number(formData.discount);
    if (formData.type === 'percentage') {
      if (formData.discount === '' || formData.discount === null || isNaN(discountVal)) {
        newErrors.discount = 'Discount percentage is required';
      } else if (discountVal <= 0 || discountVal > 100) {
        newErrors.discount = 'Enter percentage between 1 and 100';
      }
      if (formData.maxDiscount !== '' && formData.maxDiscount !== null) {
        const md = Number(formData.maxDiscount);
        if (isNaN(md) || md < 0) newErrors.maxDiscount = 'Max discount must be a non-negative number';
      }
    } else if (formData.type === 'fixed' || formData.type === 'flat' ) {
      if (formData.discount === '' || formData.discount === null || isNaN(discountVal)) {
        newErrors.discount = 'Discount amount is required';
      } else if (discountVal <= 0) {
        newErrors.discount = 'Discount must be greater than 0';
      }
    } else {
      if (formData.discount !== '' && formData.discount !== null && isNaN(discountVal)) {
        newErrors.discount = 'Discount must be numeric';
      }
    }

    const minOrder = Number(formData.minOrderAmount);
    if (formData.minOrderAmount === '' || formData.minOrderAmount === null || isNaN(minOrder)) {
      newErrors.minOrderAmount = 'Minimum order amount is required';
    } else if (minOrder < 0) {
      newErrors.minOrderAmount = 'Minimum order amount cannot be negative';
    }

    const usageLimit = Number(formData.usageLimit);
    const perUserLimit = Number(formData.perUserLimit);
    if (formData.usageLimit === '' || formData.usageLimit === null || isNaN(usageLimit)) {
      newErrors.usageLimit = 'Usage limit is required';
    } else if (!Number.isInteger(usageLimit) || usageLimit <= 0) {
      newErrors.usageLimit = 'Usage limit must be a positive integer';
    }
    if (formData.perUserLimit === '' || formData.perUserLimit === null || isNaN(perUserLimit)) {
      newErrors.perUserLimit = 'Per user limit is required';
    } else if (!Number.isInteger(perUserLimit) || perUserLimit <= 0) {
      newErrors.perUserLimit = 'Per user limit must be a positive integer';
    } else if (!newErrors.usageLimit && perUserLimit > usageLimit) {
      newErrors.perUserLimit = 'Per user limit cannot exceed overall usage limit';
    }

    if (!formData.validFrom) {
      newErrors.validFrom = 'Valid from date is required';
    }
    if (!formData.validTill) {
      newErrors.validTill = 'Valid till date is required';
    }
    if (formData.validFrom && formData.validTill) {
      const from = new Date(formData.validFrom).getTime();
      const till = new Date(formData.validTill).getTime();
      if (isNaN(from) || isNaN(till)) {
        if (isNaN(from)) newErrors.validFrom = 'Invalid start date';
        if (isNaN(till)) newErrors.validTill = 'Invalid end date';
      } else if (from >= till) {
        newErrors.validTill = 'Valid Till must be after Valid From';
      }
    }

    if (!formData.applicableTo || !formData.applicableTo.toString().trim()) {
      newErrors.applicableTo = 'Please select applicable audience';
    }

    if (!formData.applicableCardTypes || (Array.isArray(formData.applicableCardTypes) && formData.applicableCardTypes.length === 0) || (typeof formData.applicableCardTypes === 'string' && !formData.applicableCardTypes.trim())) {
      newErrors.applicableCardTypes = 'Enter at least one card type';
    }

    const bins = Array.isArray(formData.allowedCardBins) ? formData.allowedCardBins : (typeof formData.allowedCardBins === 'string' ? formData.allowedCardBins.split(',').map(b => b.trim()).filter(Boolean) : []);
    if (bins.length === 0) {
      newErrors.allowedCardBins = 'Enter at least one BIN (6 digits recommended)';
    } else {
      const invalidBins = bins.filter(b => !/^\d{4,6}$/.test(b));
      if (invalidBins.length > 0) {
        newErrors.allowedCardBins = `Invalid BIN(s): ${invalidBins.join(', ')} — use 4 to 6 digits`;
      }
    }

    if (!['active','inactive','expired'].includes(formData.status)) {
      newErrors.status = 'Invalid status selected';
    }

    if (formData.role_id && formData.assignTo) {
      const found = (users || []).find(u => (u._id || u.id) === formData.assignTo);
      if (found) {
        const userRoleId = typeof found.role_id === 'object' && found.role_id !== null ? (found.role_id._id || found.role_id.id) : found.role_id;
        if (userRoleId !== formData.role_id) {
          newErrors.assignTo = 'Selected user does not belong to selected role';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    setErrors(prev => {
      const next = { ...prev };
      if (next.code && formData.code && formData.code.toString().trim()) delete next.code;
      if (next.discount && formData.discount !== '' && !isNaN(Number(formData.discount))) delete next.discount;
      if (next.maxDiscount && formData.maxDiscount !== '' && !isNaN(Number(formData.maxDiscount))) delete next.maxDiscount;
      if (next.minOrderAmount && formData.minOrderAmount !== '' && !isNaN(Number(formData.minOrderAmount))) delete next.minOrderAmount;
      if (next.usageLimit && formData.usageLimit !== '' && Number.isInteger(Number(formData.usageLimit)) && Number(formData.usageLimit) > 0) delete next.usageLimit;
      if (next.perUserLimit && formData.perUserLimit !== '' && Number.isInteger(Number(formData.perUserLimit)) && Number(formData.perUserLimit) > 0) delete next.perUserLimit;
      if ((next.validFrom || next.validTill) && formData.validFrom && formData.validTill) {
        const from = new Date(formData.validFrom).getTime();
        const till = new Date(formData.validTill).getTime();
        if (!isNaN(from) && !isNaN(till) && from < till) {
          delete next.validFrom;
          delete next.validTill;
        }
      }
      if (next.applicableTo && formData.applicableTo) delete next.applicableTo;
      if (next.applicableCardTypes && formData.applicableCardTypes && (Array.isArray(formData.applicableCardTypes) ? formData.applicableCardTypes.length > 0 : formData.applicableCardTypes)) delete next.applicableCardTypes;
      if (next.allowedCardBins) {
        const bins = Array.isArray(formData.allowedCardBins) ? formData.allowedCardBins : (typeof formData.allowedCardBins === 'string' ? formData.allowedCardBins.split(',').map(b => b.trim()).filter(Boolean) : []);
        const invalidBins = bins.filter(b => !/^\d{4,6}$/.test(b));
        if (bins.length > 0 && invalidBins.length === 0) delete next.allowedCardBins;
      }
      if (next.status && ['active','inactive','expired'].includes(formData.status)) delete next.status;
      if (next.assignTo && formData.assignTo) delete next.assignTo;

      return next;
    });
  }, [formData, users]);

  const handleSubmit = async (e) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    if (!validateForm()) return;
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    
    try {
      const tokenValue = sessionStorage.getItem('token');
      const token = tokenValue ? `Bearer ${tokenValue}` : '';
      
      const payload = {
        ...formData,
        discount: Number(formData.discount),
        maxDiscount: (formData.maxDiscount !== '' && formData.maxDiscount !== null) ? Number(formData.maxDiscount) : null,
        minOrderAmount: Number(formData.minOrderAmount),
        usageLimit: formData.usageLimit ? Number(formData.usageLimit) : null,
        perUserLimit: formData.perUserLimit ? Number(formData.perUserLimit) : null,
        allowedCardBins: (Array.isArray(formData.allowedCardBins) ? formData.allowedCardBins : (typeof formData.allowedCardBins === 'string' ? formData.allowedCardBins.split(',').map(b => b.trim()).filter(Boolean) : [])).filter(bin => bin.trim() !== ''),
        role_id: formData.role_id || null,
        assignTo: formData.assignTo || null
      };

      let response;
      if (isEditing) {
        response = await putData(`coupon/update/${currentCoupon.id}`, payload, token);
      } else {
        response = await postData('coupon/add', payload, token);
      }
      
      if (response) {
        await fetchCoupons(meta.page, meta.limit);
        handleCloseForm();
      }
    } catch (error) {
      console.error('Error submitting coupon:', error);
      setError('Failed to submit coupon. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const tokenValue = sessionStorage.getItem('token');
      const token = tokenValue ? `Bearer ${tokenValue}` : '';
      const response = await deleteData(`coupon/delete/${id}`, token);
      if (response) {
        setCoupons(coupons.filter(coupon => coupon.id !== id));
        fetchCoupons(meta.page, meta.limit);
      } else {
        setError('Failed to delete coupon');
      }
    } catch (error) {
      setError('Failed to delete coupon. Please try again.');
    }
  };

  const filteredCoupons = coupons.filter(coupon => {
    const matchesSearch = coupon.code.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filter === 'All' || coupon.status === filter;
    return matchesSearch && matchesFilter;
  });

  const getStatusConfig = (status) => {
    switch (status) {
      case 'active': 
        return { color: 'success', text: 'Active' };
      case 'expired': 
        return { color: 'error', text: 'Expired' };
      default: 
        return { color: 'default', text: 'Inactive' };
    }
  };

  const formatDiscount = (coupon) => {
    if (coupon.type === 'percentage') return `${coupon.discount}% OFF`;
    if (coupon.type === 'flat' || coupon.type === 'fixed') return `₹${coupon.discount} OFF`;
    return `${coupon.discount}`;
  };

  const handlePageChange = (event, newPage) => {
    setMeta(prev => ({ ...prev, page: newPage }));
  };

  const formatToDDMMYYYY = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const renderViewField = (label, value) => (
    <Box sx={{ p: 1 }}>
      <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Mulish, sans-serif' }}>{label}</Typography>
      <Typography sx={{ fontFamily: 'Mulish, sans-serif', fontWeight: 500 }}>{value || '-'}</Typography>
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', p: 3 }}>
      <Box sx={{ maxWidth: '1400px', mx: 'auto' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}
        
        {showForm ? (
          <Box>
            <FormContainer>
               {/* Form Header */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', fontFamily: 'Mulish, sans-serif' }}>
                  {isEditing ? (formMode === 'edit' ? 'Edit Coupon' : 'View Coupon') : 'Add New Coupon'}
                </Typography>

                <Box display="flex" alignItems="center" gap={2}>
                  {isEditing && (
                    <ToggleButton
                      value={formMode}
                      onClick={handleFormModeChange}
                      size="small"
                      sx={{
                        fontFamily: 'Mulish, sans-serif',
                        color: '#495057',
                        borderColor: '#dee2e6',
                        textTransform: 'none',
                        '&.Mui-selected': { bgcolor: '#e9ecef' }
                      }}
                    >
                      {formMode === 'view' ? <><FaEdit style={{ marginRight: 8 }} /> Edit</> : <><FaEye style={{ marginRight: 8 }} /> View</>}
                    </ToggleButton>
                  )}
                  <Button
                    variant="outlined"
                    onClick={handleCloseForm}
                    sx={{
                      color: '#dc3545',
                      borderColor: '#dc3545',
                      fontFamily: 'Mulish, sans-serif',
                      textTransform: 'none',
                      '&:hover': { bgcolor: 'rgba(220, 53, 69, 0.05)', borderColor: '#dc3545' },
                    }}
                  >
                    Back
                  </Button>
                </Box>
              </Box>
              
              {/* Form Content */}
              <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                   <Grid item xs={12} sm={6}>{(formMode === 'view' ? renderViewField('Code', formData.code) : <TextField fullWidth label="Code" name="code" value={formData.code} onChange={handleInputChange} required variant="outlined" sx={{ '& .MuiInputBase-root':{fontFamily:'Mulish'}}} error={!!errors.code} helperText={errors.code} />)}</Grid>
                   
                   <Grid item xs={12} sm={6}>{(formMode === 'view' ? renderViewField('Type', formData.type) : <FormControl fullWidth><InputLabel>Type</InputLabel><Select name="type" value={formData.type} onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value, discount: '', maxDiscount: '' }))} label="Type"><MenuItem value="percentage">Percentage</MenuItem><MenuItem value="fixed">Flat</MenuItem></Select></FormControl>)}</Grid>
                   
                   <Grid item xs={12} sm={6}>{(formMode === 'view' ? renderViewField('Discount', formData.discount) : <TextField fullWidth label="Discount" name="discount" value={formData.discount} onChange={handleInputChange} required variant="outlined" type="number" error={!!errors.discount} helperText={errors.discount} InputProps={{ endAdornment: formData.type === 'percentage' ? <InputAdornment position="end">%</InputAdornment> : formData.type === 'fixed' ? <InputAdornment position="end">₹</InputAdornment> : null }} />)}</Grid> 
                   
                   <Grid item xs={12} sm={6}>{(formMode === 'view' ? renderViewField('Min Order', formData.minOrderAmount) : <TextField fullWidth label="Min Order" name="minOrderAmount" value={formData.minOrderAmount} onChange={handleInputChange} required variant="outlined" type="number" error={!!errors.minOrderAmount} helperText={errors.minOrderAmount} InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} />)}</Grid> 
                   
                   {formData.type === 'percentage' && (
                     <Grid item xs={12} sm={6}>{(formMode === 'view' ? renderViewField('Max Discount', formData.maxDiscount) : <TextField fullWidth label="Max Discount" name="maxDiscount" value={formData.maxDiscount} onChange={handleInputChange} type="number" error={!!errors.maxDiscount} helperText={errors.maxDiscount} InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} />)}</Grid>
                   )}

                   <Grid item xs={12} sm={6}>{(formMode === 'view' ? renderViewField('Usage Limit', formData.usageLimit) : <TextField fullWidth label="Usage Limit" name="usageLimit" value={formData.usageLimit} onChange={handleInputChange} required type="number" error={!!errors.usageLimit} helperText={errors.usageLimit}/>)}</Grid>
                   
                   <Grid item xs={12} sm={6}>{(formMode === 'view' ? renderViewField('Per User Limit', formData.perUserLimit) : <TextField fullWidth label="Per User Limit" name="perUserLimit" value={formData.perUserLimit} onChange={handleInputChange} required type="number" error={!!errors.perUserLimit} helperText={errors.perUserLimit}/>)}</Grid>
                   
                   <Grid item xs={12} sm={6}>{(formMode === 'view' ? renderViewField('Valid From', formatToDDMMYYYY(formData.validFrom)) : <Box><TextField fullWidth label="Valid From" name="validFrom" type="datetime-local" value={formData.validFrom} onChange={handleInputChange} InputLabelProps={{ shrink: true }} required error={!!errors.validFrom} helperText={errors.validFrom}/>{formData.validFrom && !errors.validFrom && <Typography variant="caption" color="primary" sx={{ mt: 0.5, display: 'block', fontWeight: 600 }}>Format: {formatToDDMMYYYY(formData.validFrom)}</Typography>}</Box>)}</Grid>
                   
                   <Grid item xs={12} sm={6}>{(formMode === 'view' ? renderViewField('Valid Till', formatToDDMMYYYY(formData.validTill)) : <Box><TextField fullWidth label="Valid Till" name="validTill" type="datetime-local" value={formData.validTill} onChange={handleInputChange} InputLabelProps={{ shrink: true }} required error={!!errors.validTill} helperText={errors.validTill}/>{formData.validTill && !errors.validTill && <Typography variant="caption" color="primary" sx={{ mt: 0.5, display: 'block', fontWeight: 600 }}>Format: {formatToDDMMYYYY(formData.validTill)}</Typography>}</Box>)}</Grid>
                   
                   {/* Status and First Time User */}
                   <Grid item xs={12} sm={3}>{(formMode === 'view' ? renderViewField('Status', formData.status.charAt(0).toUpperCase() + formData.status.slice(1)) : <FormControl fullWidth><InputLabel>Status</InputLabel><Select name="status" value={formData.status} onChange={handleInputChange} label="Status"><MenuItem value="active">Active</MenuItem><MenuItem value="inactive">Inactive</MenuItem><MenuItem value="expired">Expired</MenuItem></Select></FormControl>)}</Grid>
                    <Grid item xs={12} sm={3}>
                        {formMode === 'view' ? (
                        renderViewField('First Time Users Only', formData.isFirstTimeUserOnly ? 'Yes' : 'No')
                        ) : (
                        <FormControlLabel
                            control={
                            <Switch
                                checked={formData.isFirstTimeUserOnly}
                                onChange={(e) => setFormData((prev) => ({ ...prev, isFirstTimeUserOnly: e.target.checked }))}
                                name="isFirstTimeUserOnly"
                            />
                            }
                            label="First Time Users Only"
                        />
                        )}
                    </Grid>

                   {/* Roles and Assignment */}
                    <Grid item xs={12} sm={6}>
                        {formMode === 'view' ? (
                        renderViewField('Role', (roles.find(r => r._id === formData.role_id)?.name) || '-')
                        ) : (
                        <FormControl fullWidth>
                            <InputLabel>Role</InputLabel>
                            <Select name="role_id" value={formData.role_id} onChange={handleInputChange} label="Role">
                            {roles && roles.map((role) => (
                                <MenuItem key={role._id || role.id} value={role._id || role.id}>
                                {(role.name || '').charAt(0).toUpperCase() + (role.name || '').slice(1)}
                                </MenuItem>
                            ))}
                            </Select>
                        </FormControl>
                        )}
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        {formMode === 'view' ? (
                        renderViewField('Assign To', (() => { const u = users.find(u => (u._id || u.id) === formData.assignTo); return u?.name || u?.email || '-'; })())
                        ) : (
                        <FormControl fullWidth error={!!errors.assignTo}>
                            <InputLabel>Assign To</InputLabel>
                            <Select name="assignTo" value={formData.assignTo} onChange={handleInputChange} label="Assign To" disabled={!formData.role_id}>
                            {!formData.role_id ? <MenuItem disabled>Select role first</MenuItem> : filteredUsers.map((user) => (
                                <MenuItem key={user._id || user.id} value={user._id || user.id}>{user.name || user.email}</MenuItem>
                            ))}
                            </Select>
                        </FormControl>
                        )}
                    </Grid>

                   <Grid item xs={12} sm={6}>{(formMode === 'view' ? renderViewField('Applicable To', formData.applicableTo) : <FormControl fullWidth error={!!errors.applicableTo}><InputLabel>Applicable To</InputLabel><Select name="applicableTo" value={formData.applicableTo} onChange={handleInputChange} label="Applicable To"><MenuItem value="all">All</MenuItem><MenuItem value="b2b">B2B</MenuItem><MenuItem value="b2c">B2C</MenuItem></Select></FormControl>)}</Grid>
                   
                   <Grid item xs={12} sm={12}>{(formMode === 'view' ? renderViewField('Applicable Card Types', Array.isArray(formData.applicableCardTypes) ? formData.applicableCardTypes.join(', ') : formData.applicableCardTypes) : <TextField fullWidth label="Applicable Card Types" name="applicableCardTypes" value={Array.isArray(formData.applicableCardTypes) ? formData.applicableCardTypes.join(', ') : formData.applicableCardTypes} onChange={(e) => setFormData(prev => ({ ...prev, applicableCardTypes: e.target.value.split(',').map(v => v.trim()).filter(Boolean) }))} error={!!errors.applicableCardTypes} helperText={errors.applicableCardTypes} />)}</Grid>

                   <Grid item xs={12} sm={12}>{(formMode === 'view' ? renderViewField('Allowed Card Bins', Array.isArray(formData.allowedCardBins) ? formData.allowedCardBins.join(', ') : formData.allowedCardBins) : <TextField fullWidth label="Allowed Card Bins" name="allowedCardBins" value={Array.isArray(formData.allowedCardBins) ? formData.allowedCardBins.join(', ') : formData.allowedCardBins} onChange={(e) => setFormData(prev => ({ ...prev, allowedCardBins: e.target.value.split(',').map(v => v.trim()) }))} error={!!errors.allowedCardBins} helperText={errors.allowedCardBins || "Enter first 4-6 digits of card numbers, separated by commas"} />)}</Grid>
                   
                   <Grid item xs={12}>
                     <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                       <Button onClick={handleCloseForm} variant="outlined" color="error" sx={{ textTransform: 'none' }}>Cancel</Button>
                       {formMode === 'edit' && <Button type="submit" variant="contained" disabled={isSubmitting} sx={{ bgcolor: '#2B333A', color: 'white', '&:hover': { bgcolor: '#1a1f24' }, textTransform: 'none' }}>{isSubmitting ? 'Saving...' : (isEditing ? 'Update Coupon' : 'Create Coupon')}</Button>}
                     </Box>
                   </Grid>
                </Grid>
              </form>
            </FormContainer>
          </Box>
        ) : (
          <Box>
            {/* Main Header and Controls */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mb: 4 }}>
              <Typography variant="h5" sx={{ fontFamily: 'Mulish, sans-serif', fontWeight: 700, color: "#1a1a1a" }}>
                Coupons Management
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                <TextField
                  variant="outlined"
                  size="small"
                  placeholder="Search coupons..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  sx={{ width: { xs: '100%', md: '350px' }, '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: 'white' } }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <FaSearch style={{ color: '#adb5bd' }} />
                      </InputAdornment>
                    ),
                  }}
                />
                
                <Box sx={{ display: 'flex', gap: 2, width: { xs: '100%', md: 'auto' } }}>
                   <FormControl variant="outlined" size="small" sx={{ minWidth: 150, '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: 'white' } }}>
                    <InputLabel>Filter Status</InputLabel>
                    <Select value={filter} onChange={handleFilterChange} label="Filter Status">
                      <MenuItem value="All">All Status</MenuItem>
                       <MenuItem value="active">Active</MenuItem>
                       <MenuItem value="inactive">Inactive</MenuItem>
                       <MenuItem value="expired">Expired</MenuItem>
                    </Select>
                  </FormControl>
                  <Button
                    variant="contained"
                    startIcon={<FaGift />}
                    onClick={handleOpenAddForm}
                    sx={{
                      fontFamily: 'Mulish, sans-serif',
                      bgcolor: '#2B333A',
                      color: 'white',
                      textTransform: 'none',
                      borderRadius: '8px',
                      fontWeight: 600,
                      px: 3,
                      boxShadow: '0 4px 12px rgba(43, 51, 58, 0.2)',
                      '&:hover': { bgcolor: '#1a1f24', boxShadow: '0 6px 16px rgba(43, 51, 58, 0.3)' }
                    }}
                  >
                    Add Coupon
                  </Button>
                </Box>
              </Box>
            </Box>

            {/* Coupons Grid */}
            {loading ? (
              <Grid container spacing={3}>
                {Array.from({ length: 6 }).map((_, idx) => (
                   <Grid item xs={12} md={6} lg={4} key={idx}>
                     <Skeleton variant="rectangular" height={160} sx={{ borderRadius: '16px' }} />
                   </Grid>
                ))}
              </Grid>
            ) : filteredCoupons.length === 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '300px', opacity: 0.7 }}>
                <img src="/img/nodata.png" alt="No data" style={{ width: '150px', marginBottom: '16px' }} />
                <Typography variant="h6" color="textSecondary" sx={{ fontFamily: 'Mulish, sans-serif' }}>No coupons found</Typography>
              </Box>
            ) : (
              <Grid container spacing={3}>
                {filteredCoupons.map((coupon) => {
                  const status = getStatusConfig(coupon.status);
                  return (
                    <Grid item xs={12} md={6} lg={4} key={coupon.id}>
                      <VoucherCard onClick={() => handleOpenEditForm(coupon)}>
                        <CardLeftSection>
                          <img src="/img/gift.png" alt="Gift" style={{ width: '60px', opacity: 0.9 }} />
                        </CardLeftSection>
                        
                        <CardRightSection>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                             <Box>
                                <Typography variant="caption" sx={{ color: '#6c757d', fontFamily: 'Mulish, sans-serif', display: 'block', mb: 0.5 }}>
                                  CODE
                                </Typography>
                                <CouponCodeBox>
                                  {coupon.code}
                                </CouponCodeBox>
                             </Box>
                             <Chip 
                              label={status.text} 
                              size="small" 
                              color={status.color} 
                              sx={{ 
                                height: '22px', 
                                fontSize: '0.7rem', 
                                fontFamily: 'Mulish, sans-serif', 
                                fontWeight: 700 
                              }} 
                            />
                          </Box>

                          <Box sx={{ my: 1 }}>
                            <Typography variant="h5" sx={{ fontWeight: 800, color: '#212529', fontFamily: 'Mulish, sans-serif' }}>
                              {formatDiscount(coupon)}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#6c757d', fontFamily: 'Mulish, sans-serif' }}>
                              Min Order: ₹{coupon.minOrderAmount}
                            </Typography>
                          </Box>

                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
                             <Typography variant="caption" sx={{ color: '#999', fontFamily: 'Mulish, sans-serif' }}>
                               Valid till: {new Date(coupon.validTill).toLocaleDateString()}
                             </Typography>
                             <IconButton 
                               size="small"
                               onClick={(e) => { e.stopPropagation(); handleDelete(coupon.id); }}
                               sx={{ 
                                 color: '#aaa',
                                 '&:hover': { color: '#dc3545', bgcolor: 'rgba(220, 53, 69, 0.1)' }
                               }}
                             >
                               <MdDeleteForever size={20} />
                             </IconButton>
                          </Box>
                        </CardRightSection>
                      </VoucherCard>
                    </Grid>
                  );
                })}
              </Grid>
            )}

            {meta.total > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <Pagination
                  count={meta.totalPages}
                  page={meta.page}
                  onChange={handlePageChange}
                  color="primary"
                  shape="rounded"
                  showFirstButton
                  showLastButton
                />
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default CouponsManagement;
